import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { useAuth } from "./useAuth"
import { toast } from "sonner"

export interface Supplier {
  id: string
  condominium_id: string
  name: string
  document?: string
  email?: string
  phone?: string
  address?: string
  category: string
  notes?: string
  is_active: boolean
  created_by?: string
  created_at: string
  updated_at: string
}

export const useSuppliers = () => {
  const { profile } = useAuth()
  
  return useQuery({
    queryKey: ["suppliers", profile?.condominium_id],
    queryFn: async () => {
      if (!profile?.condominium_id) return []

      const { data, error } = await supabase
        .from("suppliers")
        .select("*")
        .eq("condominium_id", profile.condominium_id)
        .order("name", { ascending: true })

      if (error) throw error
      return data as Supplier[]
    },
    enabled: !!profile?.condominium_id
  })
}

export const useCreateSupplier = () => {
  const queryClient = useQueryClient()
  const { profile } = useAuth()

  return useMutation({
    mutationFn: async (supplierData: Omit<Supplier, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'condominium_id'>) => {
      if (!profile?.id || !profile?.condominium_id) {
        throw new Error("Usuário não autenticado")
      }

      const { data, error } = await supabase
        .from("suppliers")
        .insert({
          ...supplierData,
          created_by: profile.id,
          condominium_id: profile.condominium_id
        })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["suppliers"] })
      toast.success("Fornecedor cadastrado com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao cadastrar fornecedor:", error)
      toast.error("Erro ao cadastrar fornecedor")
    }
  })
}

export const useUpdateSupplier = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Supplier> }) => {
      const { data, error } = await supabase
        .from("suppliers")
        .update(updates)
        .eq("id", id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["suppliers"] })
      toast.success("Fornecedor atualizado com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao atualizar fornecedor:", error)
      toast.error("Erro ao atualizar fornecedor")
    }
  })
}

export const useDeleteSupplier = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("suppliers")
        .delete()
        .eq("id", id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["suppliers"] })
      toast.success("Fornecedor removido com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao remover fornecedor:", error)
      toast.error("Erro ao remover fornecedor")
    }
  })
}
